import { useEffect, useState, useMemo } from "react";
import {
  Box,
  Button,
  Typography,
  Paper,
  Stack,
  Tabs,
  Tab,
  TextField,
  InputAdornment,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { PrivateMainLayout } from "@/components/layout";
import { isMobile } from "react-device-detect";
import { CircularLoading, LargeContentBox } from "@/components/common";
import { Checklist } from "@mui/icons-material";

import { useOrganizationContext } from "@/contexts/OrganizationContext";
import { useOrganizationAffiliation } from "@/services/fhir/organizationaffiliation/useOrganizationAffiliation";

import { GuideEmissionTab } from "./components/GuideEmissionTab";
import { useLocationContext } from "@/contexts/LocationContext";
import { useAppointmentStats } from "@/services/fhir/statistics/hooks/useAppointmentStats";
import { PageHeader } from "@/components/common/PageHeader";
import { useDocumentReferenceTiss } from "@/services/tiss/useDocumentReferenceTiss";

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`insurance-tabpanel-${index}`}
      aria-labelledby={`insurance-tab-${index}`}
      {...other}
    >
      {value === index && <Box>{children}</Box>}
    </div>
  );
}

interface Filters {
  organization: string;
  plan: string;
  procedure: string;
}

// Componente principal da tela de planos de saúde
export function InsuranceGuide() {
  const { organization } = useOrganizationContext();
  const { location } = useLocationContext();
  const [currentTab, setCurrentTab] = useState(0);
  const [refetch, setRefetch] = useState(false);
  const [filters, setFilters] = useState<Filters>({
    organization: "",
    plan: "",
    procedure: "",
  });

  const {
    organizationAffiliation,
    primaryOrganizations,
    participatingOrganizations,
    loading,
    fetchOrganizationAffiliation,
  } = useOrganizationAffiliation();

  // Hook de estatísticas de agendamentos
  const {
    data: appointmentData,
    loading: appointmentLoading,
    error: appointmentError,
    fetchStats,
  } = useAppointmentStats({
    status: ["fulfilled"],
    actors:
      location?.map((loc) => `Location/${loc.resource?.id}`).filter(Boolean) ||
      [],
    supportingInfoMissing: true,
    summary: false,
  });

  // Hook para buscar guias TISS
  const {
    documentReferences,
    loading: documentLoading,
    fetchDocumentReferenceTiss,
  } = useDocumentReferenceTiss();

  // Função para filtrar appointments baseado na tab selecionada e adicionar documentReferences
  const filteredAppointments = useMemo(() => {
    if (!appointmentData?.appointments) return [];

    const appointmentDocRefs = new Map<string, fhir5.DocumentReference>();
    documentReferences?.forEach((doc) => {
      if (doc.context && Array.isArray(doc.context)) {
        const reference = doc.context[0]?.reference;
        if (typeof reference === "string") {
          const appointmentId = reference.replace("Appointment/", "");
          appointmentDocRefs.set(appointmentId, doc);
        }
      }
    });

    // Se estiver na tab de guias emitidas (index 1), mostra appointments com guias
    // Se estiver na tab de guias pendentes (index 0), mostra appointments sem guias
    return appointmentData.appointments.filter((app) =>
      currentTab === 1
        ? appointmentDocRefs.has(app.id || "")
        : !appointmentDocRefs.has(app.id || "")
    );
  }, [documentReferences, appointmentData?.appointments, currentTab]);

  // Handlers
  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setCurrentTab(newValue);
  };

  const handleFilterChange = (field: keyof Filters, value: string) => {
    setFilters((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  // Effects
  useEffect(() => {
    if (organization?.id) {
      fetchOrganizationAffiliation({
        primaryOrganization: organization.id,
        count: 50,
        includePrimaryOrg: true,
        includeParticipatingOrg: true,
      });
    }
  }, [organization?.id]);

  useEffect(() => {
    if (location?.length) {
      fetchStats();
      fetchDocumentReferenceTiss({
        organization: organization?.id,
        count: 100,
      });
    }
  }, [currentTab, location, fetchStats, fetchDocumentReferenceTiss, refetch]);

  return (
    <LargeContentBox>
      <Stack sx={{ width: "100%" }} spacing={3}>
        <PageHeader
          title="Emissão de Guias"
          icon={Checklist}
          description="Emissão e gerenciamento de guias TISS"
        />

        {/* Filtros */}
        <Paper sx={{ p: 2, mb: 3 }}>
          <Typography variant="subtitle2" gutterBottom>
            Filtros
          </Typography>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
            <TextField
              fullWidth
              variant="outlined"
              size="small"
              placeholder="Filtrar por operadora..."
              value={filters.organization}
              onChange={(e) =>
                handleFilterChange("organization", e.target.value)
              }
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              fullWidth
              variant="outlined"
              size="small"
              placeholder="Filtrar por plano..."
              value={filters.plan}
              onChange={(e) => handleFilterChange("plan", e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              fullWidth
              variant="outlined"
              size="small"
              placeholder="Filtrar por procedimento..."
              value={filters.procedure}
              onChange={(e) => handleFilterChange("procedure", e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
          </Stack>
        </Paper>

        {loading || appointmentLoading || documentLoading ? (
          <Stack
            height="100%"
            alignItems="center"
            justifyContent="center"
            spacing={2}
            p={3}
          >
            <CircularLoading />
          </Stack>
        ) : (
          <>
            <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
              <Tabs value={currentTab} onChange={handleTabChange}>
                <Tab label="Guias pendentes" />
                <Tab label="Guias emitidas" />
              </Tabs>
            </Box>

            {[0, 1].map((tabIndex) => (
              <TabPanel key={tabIndex} value={currentTab} index={tabIndex}>
                <GuideEmissionTab
                  refetch={() => setRefetch(!refetch)}
                  loading={appointmentLoading || documentLoading}
                  error={appointmentError}
                  appointments={filteredAppointments}
                  patients={appointmentData?.patients || []}
                  organizationAffiliation={organizationAffiliation}
                  primaryOrganizations={primaryOrganizations}
                  participatingOrganizations={participatingOrganizations}
                  documentReferences={documentReferences}
                  filters={filters}
                  isEmittedGuides={tabIndex === 1}
                />
              </TabPanel>
            ))}
          </>
        )}
      </Stack>
    </LargeContentBox>
  );
}
